// TODO: Assessment remote cleanup, delete this file. See "Cleanup" in docs/RemoteModules.md
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { TAvailabilityCollectionSettings } from 'types/availabilityCollectionSettings.type';
import type { IDefaultReducerState } from 'types/state.interface';
import { errorNotification } from 'utils/notifications';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { validateAvailabilitySettingSchema } from 'utils/validations/availabilityCollectionSettings.validation';
import api from '../services/api.service';

export type TAvailabilityCollectionSettingsState = IDefaultReducerState & {
  data: TAvailabilityCollectionSettings;
};

export const initialState: TAvailabilityCollectionSettingsState = {
  loading: false,
  hasErrors: false,
  data: {
    reservationMode: undefined,
    reservationTemplate: undefined,
    availabilityType: 'AVAILABILITY', // Default value
  },
};

// Slice
const slice = createSlice({
  name: 'availabilityCollectionMapping',
  initialState,
  reducers: {
    fetchAvailabilityCollectionSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchAvailabilityCollectionSettingsSuccess: (
      state,
      { payload }: PayloadAction<TAvailabilityCollectionSettings>
    ) => {
      state.data = {
        reservationMode: payload.reservationMode,
        reservationTemplate: payload.reservationTemplate,
        availabilityType: payload.availabilityType || 'AVAILABILITY', // Default to 'AVAILABILITY'
      };
      finishedLoadingSuccess(state);
    },
    fetchAvailabilityCollectionSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    saveAvailabilityCollectionSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    saveAvailabilityCollectionSettingsSuccess: (state) => {
      finishedLoadingSuccess(state);
    },
    saveAvailabilityCollectionSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    update: (
      state,
      { payload }: PayloadAction<TAvailabilityCollectionSettings>
    ) => {
      state.data = {
        ...state.data,
        ...payload,
      };
    },
  },
});

export default slice.reducer;

// Selectors
export const availabilityCollectionSettingsSelector = (
  state: TRootState
): TAvailabilityCollectionSettings => state.availabilityCollectionSettings.data;

export const availabilityCollectionLoadingSelector = (
  state: TRootState
): boolean => state.availabilityCollectionSettings.loading;

// Actions
export const {
  // reservation settings
  fetchAvailabilityCollectionSettingsRequest,
  fetchAvailabilityCollectionSettingsSuccess,
  fetchAvailabilityCollectionSettingsFailure,
  saveAvailabilityCollectionSettingsRequest,
  saveAvailabilityCollectionSettingsSuccess,
  saveAvailabilityCollectionSettingsFailure,
  update,
} = slice.actions;

export const fetchExamV3ReservationSettings = createAppAsyncThunk(
  'availabilityCollectionSettings/fetchAvailabilityCollectionSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchAvailabilityCollectionSettingsRequest());
      const result = await api.get({
        endpoint: `/exam/availability-settings/mapping`,
      });
      dispatch(fetchAvailabilityCollectionSettingsSuccess(result));
    } catch (e) {
      dispatch(fetchAvailabilityCollectionSettingsFailure());
      console.error(e);
    }
  }
);

export const saveExamV3ReservationSettings = createAppAsyncThunk(
  'availabilityCollectionSettings/saveAvailabilityCollectionSettings',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(saveAvailabilityCollectionSettingsRequest());
      const reservationSettings =
        availabilityCollectionSettingsSelector(getState());

      const validated = validateAvailabilitySettingSchema(reservationSettings);

      if (!validated.success) {
        errorNotification(
          'Configuration could not be validated',
          validated.error.issues.map((error) => error.message)
        );
        throw new Error('Configuration could not be validated');
      }

      await api.put({
        endpoint: '/exam/availability-settings/mapping',
        data: reservationSettings,
      });
      dispatch(saveAvailabilityCollectionSettingsSuccess());
    } catch (error) {
      dispatch(saveAvailabilityCollectionSettingsFailure());
      console.error(error);
    }
  }
);
